import React, { useEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { namedRoute } from '../../../common/utils/helpers/routes';
import { selectSuggestedFeatures } from '../../../common/store/search';
import { selectActiveItem } from '../../../pages/item-details/store';
import { selectLongPressLocation } from '../../../common/store/item-edit';
import { selectZoom } from '../../store';
import SearchResultButton from '../../../common/components/search-result-button/SearchResultButton';
import {
  MIN_MAP_EDITABLE_ZOOM_LEVEL,
  INITIAL_GEOLOCATION_MAP_ZOOM,
} from '../../../common/utils/constants/constants';
import { useMobile } from '../../../common/components/responsive/Responsive';
import Overlay from '../../../common/components/overlay/Overlay';
import { useGlMap } from '../../contexts/context';
import { Marker } from '../marker/Marker';
import Pin from '../pin/Pin';
import POIIcon from '../poi-icon/POIIcon';
import GuidanceLabel from '../guidance-label/GuidanceLabel';
import LongPressAdditionControl from '../controls/long-press-location-control/LongPressLocationControl';
import MapItemEdit from '../map-item-edit/MapItemEdit';
import InteractiveLayers from '../layers/interactive-layers/InteractiveLayers';
import MapItemAdd from '../map-item-add/MapItemAdd';
import { sendReportToSentry } from '../../../common/utils/helpers/sentry';
import { useRetryingAnimationRunner } from '../../utils/hooks/useRetryingAnimationRunner';
import {
  itemFocusAnimationOptions,
  useItemFocus,
} from '../../utils/hooks/useItemFocus';
import {
  fullscreenPadding,
  sidepanelPadding,
  useMapPadding,
} from '../../utils/hooks/useMapPadding';
import { performInterruptionSafeAnimation } from '../../utils/helpers/animationUtils';
import './MapController.scss';

const searchResultsAnimationOptions = {
  animate: true,
  duration: 2000,
};

const SuggestionMarkers = () => {
  const suggestions = useSelector(selectSuggestedFeatures);
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <>
      {suggestions
        .map((item, index) => {
          const { feature_name, id, internal_id } = item.properties;
          return (
            <Marker
              key={`${id}_${internal_id}_${feature_name}`}
              location={item.geometry.coordinates}
            >
              <SearchResultButton component={Pin} item={item} index={index}>
                {index + 1}
              </SearchResultButton>
            </Marker>
          );
        })
        .reverse()}
    </>
  );
};

const MapRoot = () => {
  const searchResults = useSelector(selectSuggestedFeatures);
  const animationRunner = useRetryingAnimationRunner();
  useMapPadding({
    desktopPadding: searchResults ? sidepanelPadding : fullscreenPadding,
    animationRunner,
  });

  const zoom = useSelector(selectZoom);
  const longPressLocation = useSelector(selectLongPressLocation);

  const isMobile = useMobile();
  const glMap = useGlMap();

  useEffect(() => {
    if (!glMap) {
      return;
    }

    if (longPressLocation) {
      return animationRunner(
        (animationDuration) =>
          performInterruptionSafeAnimation(
            glMap,
            (duration) =>
              glMap.easeTo({
                center: longPressLocation,
                zoom: Math.max(MIN_MAP_EDITABLE_ZOOM_LEVEL, glMap.getZoom()),
                ...itemFocusAnimationOptions,
                duration,
              }),
            animationDuration,
          ),
        { animationDuration: itemFocusAnimationOptions.duration },
      );
    }
  }, [glMap, longPressLocation, animationRunner]);

  return (
    <>
      {(zoom < INITIAL_GEOLOCATION_MAP_ZOOM || !longPressLocation) && (
        <Overlay
          className="map__overlay"
          position={isMobile ? 'top' : 'bottom'}
        >
          <GuidanceLabel
            className="map-root__guidance-label"
            icon={
              zoom < INITIAL_GEOLOCATION_MAP_ZOOM
                ? 'magnifying-glass-add'
                : 'hand-tap'
            }
          >
            {zoom < INITIAL_GEOLOCATION_MAP_ZOOM
              ? 'Zoom in to edit objects'
              : 'Long press to contribute data'}
          </GuidanceLabel>
        </Overlay>
      )}

      {!isMobile && <SuggestionMarkers />}
      <InteractiveLayers />
      <LongPressAdditionControl />
      {longPressLocation && (
        <Marker location={longPressLocation}>
          <POIIcon />
        </Marker>
      )}
    </>
  );
};

const MapResultsList = () => {
  const animationRunner = useRetryingAnimationRunner();
  useMapPadding({ animationRunner });
  const glMap = useGlMap();
  const suggestions = useSelector(selectSuggestedFeatures);

  useEffect(() => {
    if (!glMap) {
      return;
    }

    if (!suggestions || suggestions.length === 0) {
      return;
    }

    let [minLng, minLat, maxLng, maxLat] = [
      +Infinity,
      +Infinity,
      -Infinity,
      -Infinity,
    ];
    suggestions.forEach((item) => {
      const [lng, lat] = item.geometry.coordinates;
      minLng = Math.min(minLng, lng);
      minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng);
      maxLat = Math.max(maxLat, lat);
    });

    return animationRunner(
      (animationDuration) =>
        performInterruptionSafeAnimation(
          glMap,
          (duration) => {
            try {
              glMap.fitBounds([minLng, minLat, maxLng, maxLat], {
                ...searchResultsAnimationOptions,
                duration,
              });
            } catch (error) {
              sendReportToSentry(error, {
                origin: 'MapResultsList suggestions fit bounds animation',
              });
            }
          },
          animationDuration,
        ),
      { animationDuration: searchResultsAnimationOptions.duration },
    );
  }, [glMap, suggestions, animationRunner]);

  return (
    <>
      <SuggestionMarkers />
      <InteractiveLayers />
    </>
  );
};

const MapItemDetails = () => {
  const animationRunner = useRetryingAnimationRunner();
  useMapPadding({ animationRunner });

  const item = useSelector(selectActiveItem);
  useItemFocus(item, animationRunner);

  return (
    <>
      {item && (
        <Marker location={item.geometry.coordinates}>
          <POIIcon poi={item} />
        </Marker>
      )}
      <InteractiveLayers />
    </>
  );
};

const MapController = () => {
  return (
    <>
      <Switch>
        <Route path={namedRoute('root')} exact={true}>
          <MapRoot />
        </Route>
        <Route path={namedRoute('error')} exact={true}>
          <MapRoot />
        </Route>
        <Route path={namedRoute('results-list')}>
          <MapResultsList />
        </Route>
        <Route
          path={[
            namedRoute('details'),
            namedRoute('remove-root'),
            namedRoute('remove'),
          ]}
        >
          <MapItemDetails />
        </Route>
        <Route path={namedRoute('edit')}>
          <MapItemEdit />
        </Route>
        <Route path={namedRoute('add-root')} exact={true}>
          {null}
        </Route>
        <Route path={namedRoute('add')}>
          <MapItemAdd />
        </Route>
      </Switch>
    </>
  );
};

export default MapController;
